<template>
	<div class="row inputs-containers">
		<div class="row input-container">
			<p>{{ getString('fiscal_name') }}</p>
			<AppTextInput class="ml-auto" ref="name" :placeholder="getString('name')" :value="company ? company.name : null" @changed-text="toUpdate = true" />
		</div>
		<div class="row input-container">
			<p>{{ getString('fiscal_code') }}</p>
			<AppTextInput class="ml-auto" ref="fiscal" :placeholder="getString('fiscal_code')" :value="company ? company.fiscal_code : null" @changed-text="toUpdate = true" />
		</div>
		<div class="row input-container">
			<p>{{ getString('legal_address') }}</p>
			<AppGoogleMapsInput :country-restriction="company?.country_code || COUNTRY" class="text-input ml-auto" :placeholder="getString('address')" no-map @changed-address="changedAddress" :value="company ? company.address : null" />
		</div>
		<div class="row input-container">
			<p>{{ getString('delivery_address') }}</p>
			<AppGoogleMapsInput :country-restriction="company?.country_code || COUNTRY" class="text-input ml-auto" :placeholder="getString('address')" no-map @changed-address="changedDeliveryAddress" :value="company ? company.delivery_address : null" />
		</div>
		<div class="row input-container">
			<p>{{ getString('sdi') }}</p>
			<AppTextInput class="ml-auto" ref="sdi" :placeholder="getString('sdi')" :value="company ? company.sdi : null" @changed-text="toUpdate = true" />
		</div>
		<div class="row input-container">
			<p>{{ getString('pec') }}</p>
			<AppTextInput class="ml-auto" ref="pec" :placeholder="getString('pec')" type="email" :value="company ? company.pec : null" @changed-text="toUpdate = true" />
		</div>
		<div class="row input-container">
			<p>{{ getString('marketing_consent') }}</p>
			<AppSwitch class="ml-auto" ref="privacy" :checked="company ? company.privacy : false" @switched="toUpdate = true" />
		</div>
		<div class="row input-container">
			<p>{{ getString('notes') }}</p>
			<AppTextInput class="ml-auto" ref="notes" textarea :placeholder="getString('notes')" :value="company ? company.notes : null" @changed-text="toUpdate = true" />
		</div>
		<div v-if="!this.noDelete" class="row input-container">
			<p>{{ getString('delete') }}</p>
			<AppButton class="right-item" small outline :text="getString('delete_customer')" @click="clickDeleteCompany" />
		</div>
		<AppPopup
			v-if="!this.noDelete"
			:title="getString('confirm_delete_company')"
			:body-text="getString('confirm_delete_customer_explain')"
			:buttons="popupDeleteButtons"
			ref="confirm-delete"
			prevent-overlay-dismiss
		/>
		<AppPopup
			:title="getString('same_vat_fiscal_error_title')"
			:body-text="getString('same_vat_fiscal_error_text', [this.company?.vat])"
			:buttons="popupVatFiscalButtons"
			ref="confirm-same-vat-fiscal"
		/>
	</div>
</template>

<script>
import AppTextInput from "@/shared/components/AppTextInput.vue";
import AppGoogleMapsInput from "@/shared/components/AppGoogleMapsInput.vue";
import {useLanguageStore, useNotificationStore} from "@/shared/stores";
import {useCounterStore, useCustomersStore, useUserStore} from "@/console-new/stores";
import {formatDateMMDDHHmm, formatDateTime, formatDateYYMMDD} from "@/shared/helpers/dates";
import API from "@/shared/helpers/API";
import AppButton from "@/shared/components/AppButton.vue";
import AppPopup from "@/shared/components/AppPopup.vue";
import AppSwitch from "@/shared/components/AppSwitch.vue";

export default {
	setup() {
		const languageStore = useLanguageStore();
		const userStore = useUserStore();
		const notificationStore = useNotificationStore();
		const customersStore = useCustomersStore();
		const counterStore = useCounterStore();
		return {getString: languageStore.getString, showNotification: notificationStore.showNotification, userStore, customersStore, formatDateMMDDHHmm, formatDateYYMMDD, formatDateTime, counterStore}
	},
	name: "CustomersCompanyEditDetails",
	components: {AppSwitch, AppPopup, AppButton, AppGoogleMapsInput, AppTextInput},
	props: {
		noDelete: {
			type: Boolean,
			default: false,
		},
		companyId: {
			type: Number,
			default: null,
		}
	},
	emits: ['deleted-company', 'updated-company'],
	data() {
		return {
			toUpdate: false,
			address: null,
			delivery_address: null,
			dismissedSameVatFiscal: false,
			loadingDelete: false
		}
	},
	computed: {
		popupVatFiscalButtons() {
			return [
				{text: this.getString('edit'), callback: null, negative: true},
				{text: this.getString('confirm'), callback: this.confirmSameVatFiscal},
			]
		},
		company() {
			return this.companyId ? this.customersStore.customersCompanies.find(p => p.customer_id === this.companyId) : null;
		},
		popupDeleteButtons() {
			return [
				{text: this.getString('cancel'), callback: null, negative: true, loading: this.loadingDelete},
				{text: this.getString('ok'), callback: this.deleteCompany, loading: this.loadingDelete},
			]
		}
	},
	methods: {
		confirmSameVatFiscal() {
			this.dismissedSameVatFiscal = true;
			this.$refs['confirm-same-vat-fiscal'].toggleStatus(false);
			this.updateCompany();
		},
		changedAddress(address) {
			this.address = address;
			this.toUpdate = true;
		},
		changedDeliveryAddress(address) {
			this.delivery_address = address;
			this.toUpdate = true;
		},
		updateCompany(callback = null) {
			if(this.toUpdate) {

				this.postUpdateCallback = callback;
				let name = this.$refs['name'].currentValue;
				if(!name || name.length <= 2) {
					this.showNotification("name-required");
					return;
				}

				let fiscal = this.$refs['fiscal'].currentValue;
				fiscal = fiscal && fiscal.length > 0 ? fiscal : null;
				if(fiscal && fiscal.length < 11) {
					this.showNotification("wrong-fiscal");
					return;
				}

				let sdi = this.$refs['sdi'].currentValue;
				sdi = sdi && sdi.length === 7 ? sdi : null;
				if(!sdi) {
					this.showNotification("invoice-sdi-invalid");
					return;
				}

				let address = this.address ? JSON.stringify(this.address) : null;
				let deliveryAddress = this.delivery_address ? JSON.stringify(this.delivery_address) : null;

				let pec = this.$refs['pec'].currentValue;
				pec = pec && pec.length > 0 ? pec : null;

				let notes = this.$refs['notes'].currentValue;
				notes = notes && notes.length > 0 ? notes : null;

				let privacy = this.$refs['privacy'].isChecked;

				if(!this.dismissedSameVatFiscal && this.company?.vat && fiscal && this.company.vat === fiscal) {
					this.$refs['confirm-same-vat-fiscal'].toggleStatus(true);
					return;
				}
				this.dismissedSameVatFiscal = false;

				API.init().addCustomerCompany(this.userStore.currBusinessId, this.company.customer_id, name, null, null, fiscal, sdi, address, deliveryAddress, pec, notes, privacy, (response) => {
					if(response && response.result && response.result === "OK") {
						this.showNotification("generic-success");
						this.company.name = name;
						this.company.sdi = sdi;
						this.company.fiscal_code = response.customer.cf || null;

						if(this.address) {
							this.company.address = this.address.address;
							this.company.address_id = response.address_id;
						}
						if(this.delivery_address) {
							this.company.delivery_address = this.delivery_address.address;
							this.company.delivery_address_id = response.delivery_address_id;
						}
						this.company.pec = pec;
						this.company.notes = notes;
						this.company.privacy = privacy;
						if(this.counterStore.selectedCustomer && this.counterStore.selectedCustomer.customer_id === this.company.customer_id) {
							this.counterStore.selectedCustomer.name = name;
							this.counterStore.selectedCustomer.sdi = sdi;
							this.counterStore.selectedCustomer.fiscal_code = response.customer.cf || null;
							if(this.address) {
								this.counterStore.selectedCustomer.address = this.address.address;
								this.counterStore.selectedCustomer.address_id = response.address_id;
							}
							if(this.delivery_address) {
								this.counterStore.selectedCustomer.delivery_address = this.delivery_address.address;
								this.counterStore.selectedCustomer.delivery_address_id = response.delivery_address_id;
							}
							this.counterStore.selectedCustomer.pec = pec;
							this.counterStore.selectedCustomer.notes = notes;
							this.counterStore.selectedCustomer.privacy = privacy;
						}
						if(this.counterStore.registryEditingCustomer && this.counterStore.registryEditingCustomer.customer_id === this.company.customer_id) {
							this.counterStore.registryEditingCustomer.name = name;
							this.counterStore.registryEditingCustomer.sdi = sdi;
							this.counterStore.registryEditingCustomer.fiscal_code = response.customer.cf || null;
							if(this.address) {
								this.counterStore.registryEditingCustomer.address = this.address.address;
								this.counterStore.registryEditingCustomer.address_id = response.address_id;
							}
							if(this.delivery_address) {
								this.counterStore.registryEditingCustomer.delivery_address = this.delivery_address.address;
								this.counterStore.registryEditingCustomer.delivery_address_id = response.delivery_address_id;
							}
							this.counterStore.registryEditingCustomer.pec = pec;
							this.counterStore.registryEditingCustomer.notes = notes;
							this.counterStore.registryEditingCustomer.privacy = privacy;
						}
						this.toUpdate = false;
						this.$emit('updated-company', this.companyId);
					} else {
						this.showNotification("generic-fail");
					}
					if(this.postUpdateCallback) {
						this.postUpdateCallback(response && response.result && response.result === "OK");
					}
				});

			}
		},
		clickDeleteCompany() {
			this.$refs['confirm-delete'].toggleStatus(true);
		},
		deleteCompany() {
			this.loadingDelete = true;
			API.init().deleteCustomerCompany(this.userStore.currBusinessId, this.company.customer_id, (response) => {
				this.loadingDelete = false;
				if(response && response.result && response.result === "OK") {
					this.showNotification("generic-success");
					this.$refs['confirm-delete'].toggleStatus(false);
					this.company.deleted = true;
					this.customersStore.customersCompanies = this.customersStore.customersCompanies.filter(p => p.customer_id !== this.company.customer_id);
					if(this.counterStore?.selectedCustomer) {
						this.counterStore.selectedCustomer = null;
					}
					if(this.counterStore?.registryEditingCustomer) {
						this.counterStore.registryEditingCustomer = null;
					}
					this.$emit('deleted-company', this.companyId);
				} else if(response && response.details && response.details === "not_completed_orders") {
					this.showNotification("delete-customer-open-orders-error");
				} else if(response && response.details && response.details === "not_paid_later_orders") {
					this.showNotification("delete-customer-later-orders-error");
				} else if(response && response.details && response.details === "credit_available") {
					this.showNotification("delete-customer-credit-error");
				} else if(response && response.details && response.details === "debit_left") {
					this.showNotification("delete-customer-debit-error");
				} else {
					this.showNotification("generic-fail");
				}
			});
		}
	}
}
</script>

<style scoped lang="scss">
.inputs-containers {
	display: flex;
	flex-direction: column;
	align-items: flex-end;

	.input-container {
		width: 100%;
		display: flex;
		flex-wrap: wrap;
		gap: 12px;
		justify-content: space-between;
		align-items: center;
		position: relative;
		padding: 8px 12px;

		.ml-auto {
			margin-left: auto;
		}

		&:after {
			content: "";
			position: absolute;
			top: 100%;
			left: 0;
			height: 1px;
			width: 100%;
			background-color: var(--color-alpha-3);
		}

		&:last-child {
			&:after {
				display: none;
			}
		}
	}
}
</style>